/** @jsx isolateComponent */
import { ReactNode } from 'react';

import { ColorValue, Colors, Typography } from '../../../theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';

type TagLabelColorVariant = {
  backgroundColor: string;
  color: ColorValue;
};

const TagLabelColorVariants: {
  [x: string]: TagLabelColorVariant;
} = {
  light: {
    backgroundColor: Colors.grey100,
    color: Colors.black,
  },
  dark: {
    backgroundColor: Colors.grey700,
    color: Colors.white,
  },
  blue: {
    backgroundColor: Colors.blue100,
    color: Colors.blue500,
  },
  info: {
    backgroundColor: Colors.ocean20,
    color: Colors.gray,
  },
  success: {
    backgroundColor: Colors.success10,
    color: Colors.gray,
  },
  warning: {
    backgroundColor: Colors.sun40,
    color: Colors.gray,
  },
  error: {
    backgroundColor: Colors.error10,
    color: Colors.gray,
  },
  gray: {
    backgroundColor: Colors.gray60,
    color: Colors.white,
  },
  white: {
    backgroundColor: Colors.white,
    color: Colors.gray,
  },
};

type TagLabelSizeVariant = {
  lineHeight: string;
  height: number;
};

const TagLabelSizeVariants: {
  [x: string]: TagLabelSizeVariant;
} = {
  medium: { ...Typography.allCaps800, lineHeight: '20px', height: 20 },
  small: { ...Typography.allCaps700, lineHeight: '15px', height: 15 },
};

type TagLabelProps = {
  /**
   * The color variant of the label.
   *
   * @default 'light'
   * */
  color?: keyof typeof TagLabelColorVariants & string;
  /**
   * Color themes for the TagLabel. The default value for this prop
   * should only be overridden in rare cases, and `color` should be
   * leveraged as much as possible instead.
   */
  colorTheme?: typeof TagLabelColorVariants;
  /** Text to render inside the label. */
  children: ReactNode;
  /**
   * The size variant of the label.
   *
   * @default 'medium'
   * */
  size?: keyof typeof TagLabelSizeVariants & string;
};

const defaultProps = {
  color: 'light',
  colorTheme: TagLabelColorVariants,
  size: 'medium',
};

const Styles = labelStyles('TagLabel', {
  root: (_props: TagLabelProps) => {
    const props = {
      ...defaultProps,
      ..._props,
    };
    return [
      {
        display: 'inline-block',
        padding: `0 10px`,
        borderRadius: 5,
        alignItems: 'center',
      },
      withModifier(props.color, props.colorTheme[props.color]),
      withModifier(props.size, TagLabelSizeVariants[props.size]),
    ];
  },
});

/**
 * Renders a tag-like component.
 */
export const TagLabel = (props: TagLabelProps) => (
  <span css={Styles.root(props)}>{props.children}</span>
);

TagLabel.defaultProps = defaultProps;
